import React, { useEffect, useState } from 'react'
import { X, ChevronLeft, ChevronRight, Copy, Check, BookOpen, Zap } from 'lucide-react'
import { parseSurfaceDiff, type LensConstruct } from '../utils/lensParser'
import { formatInsightToChatPrompt } from '../prompts'
import { useCopyToClipboard } from '../hooks/useCopyToClipboard'
import { useIsMobile } from '../hooks/useIsMobile'
import { useSwipeGesture } from '../hooks/useSwipeGesture'

interface SlideshowOverlayProps {
  constructs: LensConstruct[]
  currentIndex: number
  sourceLanguage: string
  targetLanguage: string
  onNext: () => void
  onPrev: () => void
  onExit: () => void
}

// Format explanation text with styled inline code (same as NotebookView)
const formatExplanationText = (text: string): string => {
  return text.replace(/`([^`]+)`/g, (_, code) => {
    return `<code style="font-family: ui-monospace, SFMono-Regular, 'JetBrains Mono', Menlo, Monaco, Consolas, monospace; font-size: 14px; background-color: rgba(0, 0, 0, 0.06); padding: 2px 6px; border-radius: 4px; font-weight: 500; letter-spacing: -0.01em;">${code}</code>`
  })
}

const SlideshowOverlay: React.FC<SlideshowOverlayProps> = ({
  constructs,
  currentIndex,
  sourceLanguage,
  targetLanguage,
  onNext,
  onPrev,
  onExit
}) => {
  const isMobile = useIsMobile()
  const construct = constructs[currentIndex]
  const { source, target } = parseSurfaceDiff(construct.surface)
  const { copied, copy: copyToClipboard } = useCopyToClipboard()

  const isFirst = currentIndex === 0
  const isLast = currentIndex === constructs.length - 1

  // Add swipe gesture support on mobile
  useSwipeGesture({
    onSwipeLeft: () => {
      if (!isLast) onNext()
    },
    onSwipeRight: () => {
      if (!isFirst) onPrev()
    },
    enabled: isMobile
  })

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    try {
      const codeSnippet = target || construct.code

      const content = formatInsightToChatPrompt({
        conceptName: construct.conceptName,
        codeElement: codeSnippet,
        sourceLanguage,
        targetLanguage,
        source: source || '(similar)',
        target: codeSnippet,
        concept: construct.concept,
        paradigm: construct.paradigm,
      })

      await copyToClipboard(content)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  // Prevent scroll on mount, restore on unmount
  useEffect(() => {
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [])

  return (
    <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col animate-in fade-in duration-200">
      {/* Header - Mobile Optimized */}
      <header className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-white/10">
        <div className="flex items-center gap-2 sm:gap-4 text-white">
          <button
            onClick={onExit}
            className="p-2 hover:bg-white/10 active:bg-white/20 rounded-lg transition-colors"
            aria-label="Exit slideshow"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="sr-only">Back to list</span>
          </button>
          <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
            <span className="font-semibold">{currentIndex + 1}/{constructs.length}</span>
            <span className="text-white/60 hidden sm:inline">·</span>
            <span className="text-white/80 hidden sm:inline truncate max-w-[200px]">{construct.conceptName}</span>
          </div>
        </div>

        <button
          onClick={onExit}
          className="p-2 hover:bg-white/10 active:bg-white/20 rounded-lg transition-colors text-white"
          aria-label="Exit slideshow"
        >
          <X className="w-5 h-5" />
        </button>
      </header>

      {/* Main Content - Mobile Optimized */}
      <main className="flex-1 flex items-center justify-center px-3 sm:px-6 py-4 sm:py-8 overflow-y-auto">
        <div className="w-full max-w-4xl">
          <div className="bg-white rounded-xl p-4 sm:p-8 shadow-2xl relative">
            {/* Copy Button - Top Right of Card - Touch-friendly on mobile */}
            <div className="absolute top-3 sm:top-6 right-3 sm:right-6 group/copy">
              <button
                onClick={handleCopy}
                className="w-10 h-10 sm:w-8 sm:h-8 p-2 hover:bg-slate-200 active:bg-slate-300 rounded-lg transition-colors flex items-center justify-center"
                style={{ minWidth: isMobile ? '40px' : '32px', minHeight: isMobile ? '40px' : '32px' }}
                aria-label="Copy insight"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-slate-400 group-hover/copy:text-slate-600" />
                )}
              </button>
              {/* Bottom tooltip */}
              {!copied && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover/copy:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                  Copy
                </div>
              )}
            </div>

            {/* Code Comparison - Stacks vertically on mobile */}
            {(source || target || construct.code) && (
              <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8 min-w-0">
                {/* Source Language */}
                <div className="space-y-2 sm:space-y-3 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center bg-blue-100 text-blue-700 px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium">
                      {sourceLanguage}
                    </span>
                    <span className="text-slate-400 text-xs sm:text-sm hidden sm:inline">You know</span>
                  </div>
                  <pre className="bg-slate-900 rounded-lg p-3 sm:p-6 text-sm sm:text-base leading-relaxed text-slate-100 font-mono overflow-x-auto border border-slate-700 max-w-full">
                    <code className="block whitespace-pre">
                      {source || '(similar)'}
                    </code>
                  </pre>
                </div>

                {/* Target Language */}
                <div className="space-y-2 sm:space-y-3 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center bg-orange-100 text-orange-700 px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium">
                      {targetLanguage}
                    </span>
                    <span className="text-slate-400 text-xs sm:text-sm hidden sm:inline">You're learning</span>
                  </div>
                  <pre className="bg-slate-900 rounded-lg p-3 sm:p-6 text-sm sm:text-base leading-relaxed text-slate-100 font-mono overflow-x-auto border border-slate-700 max-w-full">
                    <code className="block whitespace-pre">
                      {target || construct.code}
                    </code>
                  </pre>
                </div>
              </div>
            )}

            {/* Explanations - Mobile Optimized */}
            <div className="space-y-4 sm:space-y-6">
              {/* Semantic Meaning */}
              {construct.concept && (
                <div className="border-l-4 border-emerald-500 bg-emerald-50/50 pl-3 sm:pl-5 pr-3 sm:pr-5 py-3 sm:py-5 rounded-r-lg">
                  <h3 className="text-emerald-700 font-semibold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Semantic Meaning
                  </h3>
                  <div
                    className="text-slate-800 text-base leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: formatExplanationText(construct.concept)
                    }}
                  />
                </div>
              )}

              {/* Paradigm Shift */}
              {construct.paradigm && (
                <div className="border-l-4 border-purple-500 bg-purple-50/50 pl-3 sm:pl-5 pr-3 sm:pr-5 py-3 sm:py-5 rounded-r-lg">
                  <h3 className="text-purple-700 font-semibold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Paradigm Shift
                  </h3>
                  <div
                    className="text-slate-800 text-base leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: formatExplanationText(construct.paradigm)
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer Navigation - Mobile Optimized */}
      <footer className="flex items-center justify-center gap-2 sm:gap-4 px-3 sm:px-6 py-3 sm:py-6 border-t border-white/10">
        <button
          onClick={onPrev}
          disabled={isFirst}
          className="flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 sm:py-3 bg-white/10 hover:bg-white/20 active:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium text-sm sm:text-base"
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="hidden sm:inline">Previous</span>
        </button>

        {/* Progress Dots on Mobile, Keyboard hints on Desktop */}
        {isMobile ? (
          <div className="flex items-center gap-1.5 px-2">
            {constructs.map((_, idx) => (
              <div
                key={idx}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  idx === currentIndex
                    ? 'bg-white w-4'
                    : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2 px-4 text-white/60 text-sm">
            <kbd className="px-2 py-1 bg-white/10 rounded text-xs">←</kbd>
            <kbd className="px-2 py-1 bg-white/10 rounded text-xs">→</kbd>
            <span>to navigate</span>
            <span className="mx-2">·</span>
            <kbd className="px-2 py-1 bg-white/10 rounded text-xs">ESC</kbd>
            <span>to exit</span>
          </div>
        )}

        <button
          onClick={onNext}
          disabled={isLast}
          className="flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 sm:py-3 bg-white/10 hover:bg-white/20 active:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium text-sm sm:text-base"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </footer>
    </div>
  )
}

export default SlideshowOverlay
